import {
    ContentType,
    UserAccountType,
    Visibility,
    ContentWithSharesResponse,
    UserResponse,
    UserProfileStyle,
    PublicUserResponse,
    PublicContentResponseWithStatus,
    ContentDetailType,
    ShareStatus
} from "@authory-packages/core";
import { useContext } from "react";
import dynamic from "next/dynamic";
import { DomainContext } from "../../context/domainContext";
import { RequestHost } from "../../types/headers";
import { isMainDomain } from "../../utils/domainRouting";
import { getAuthorPageUrl } from "../../utils/getAuthorPageUrl";
import { getDisplayName } from "../../utils/getDisplayName";
import { getImportInProgressTitle, getImportInProgressDescription } from "./ContentDetailsPublicPageLayout.utils";
import { V3ContentRenderer } from "../../components/ContentRenderer";
import { ContentTextColumn } from "../../components/ContentRenderer/V3ContentRenderer.styles";
import { V3Loader } from "../../components/Loader";
import { OGTags, SEO_TYPE } from "../../components/OGTags";
import { V3ContentLargeWrapper } from "../../components/ReusableStyledComponents/V3ContentLargeWrapper";
import { ContactUserFormik } from "../../components/V3ContactUser";
import { V3PublicAuthorHeader } from "../../components/V3PublicAuthorHeader";
import { V3SharesReportDesktop } from "../../components/V3SharesReport";
import { VerticalSpacing } from "../../components/VerticalSpacing";
import {
    ContentLoaderWrapper,
    ProgressInderterminate,
    ProgressMaterialize,
    PublicContentWrapper,
    UserThemableArticleTitle
} from "./ContentDetailsPublicPageLayout.styles";
import { V3ContentNavbar } from "../../components/V3ContentNavbar";
import { ContentCollectionsPanel } from "../../components/ContentCollectionsPanel";
import { BannerDetails } from "../../components/BannerDetails";
import { InlineLink } from "../../components/ReusableStyledComponents/InlineLink";
import { contentHasDefaultPreview } from "../../utils/articleHasCustomPreview";
import { V3EmptyState } from "../../components/V3EmptyStateScreen/V3EmptyStateScreen";
import { V3BodyGrey48 } from "../../components/ReusableStyledComponents/V3BodyGrey48";
import { ShareSettingsTooltip } from "../../components/ReusableStyledComponents/ShareSettingsTooltip";
import { HidePrint } from "../../components/ReusableStyledComponents/HidePrint";
import { ContentGridWithFooter } from "../../components/ContentGridWithFooter";
import { ItemUrlContext } from "../../components/V3ProfileList/V3ProfileList.utils";

// Only the sidebar profile layout shows the hero, standalone item pages never do
const V4ProfileHero = dynamic(() => import("../../components/ProfileHero/V4ProfileHero").then((m) => m.V4ProfileHero));

interface ContentDetailsPublicPageLayoutProps {
    author: UserResponse | PublicUserResponse;
    content?: ContentWithSharesResponse | PublicContentResponseWithStatus;
    isPrivateView?: boolean;
    smartShareUrl?: string;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    onSubscribeFormSubmit: (email: string) => Promise<void>;
    addOrRemoveItemManuallyFromCollection?: (add: boolean, articleSlug: string, collectionSlug: string) => Promise<void>;
    onCustomPreviewOpen?: () => void;
    importInProgress?: boolean;
    isStandaloneContentItemView?: boolean;
    isStandaloneCollectionView?: boolean;
}

export const ContentDetailsPublicPageLayout = ({
    author,
    content,
    isPrivateView = false,
    smartShareUrl,
    importInProgress = false,
    onContactUserSubmit,
    onSubscribeFormSubmit,
    addOrRemoveItemManuallyFromCollection,
    onCustomPreviewOpen,
    isStandaloneContentItemView = true,
    isStandaloneCollectionView = false
}: ContentDetailsPublicPageLayoutProps) => {
    const isBusinessParent = author.type === UserAccountType.BusinessParent;
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;
    // Only a published portfolio is linked. A business parent's line already names each item's owner.
    const portfolioUrl = getAuthorPageUrl(author.profileShareStatus === ShareStatus.Published, isMainDomain(requestHostHeader), author.slug, smartShareUrl);
    const byline = !isPrivateView && !isBusinessParent && portfolioUrl ? { name: getDisplayName(author), href: portfolioUrl } : undefined;
    const isArticle = content?.article.type === ContentType.Article;
    const hasCustomPreview = contentHasDefaultPreview(content?.article);
    const useSidebarLayout =
        !isStandaloneContentItemView && !isStandaloneCollectionView && "profileStyle" in author && author.profileStyle === UserProfileStyle.sidebar;
    const isCustomAudioOrCustomVideo =
        !!content &&
        (content.article.contentDetail.type === ContentDetailType.CustomAudio ||
            content.article.contentDetail.type === ContentDetailType.CustomVideo);
    // File content (PDFs) renders a viewer that is allowed to be wider than the text column.
    const isFileContent = content?.article.contentDetail.type === ContentDetailType.File;

    return (
        <>
            {author && !isPrivateView && (
                <OGTags
                    user={author}
                    target={SEO_TYPE.ARTICLE}
                    article={content?.article}
                    context={
                        isStandaloneContentItemView
                            ? ItemUrlContext.content
                            : isStandaloneCollectionView
                              ? ItemUrlContext.collection
                              : ItemUrlContext.profile
                    }
                    forceNoIndex={!!smartShareUrl || isFileContent}
                />
            )}
            {!isPrivateView && !useSidebarLayout && (
                <V3PublicAuthorHeader
                    user={author}
                    onContactUserSubmit={onContactUserSubmit}
                    showAboutPagesNav={!isStandaloneContentItemView && !isStandaloneCollectionView}
                    smartShareUrl={smartShareUrl}
                />
            )}
            <ContentGridWithFooter
                isPrivateView={isPrivateView}
                showAuthoryBranding={author.profileAuthoryBranding}
                skipSideBarDesktop={!useSidebarLayout}
                dynamicContent={
                    <>
                        {!useSidebarLayout || isStandaloneContentItemView || isStandaloneCollectionView ? (
                            <></>
                        ) : (
                            <>
                                {"bio" in author && (
                                    <V4ProfileHero
                                        isProfileCollectionView
                                        author={author}
                                        onContactUserSubmit={onContactUserSubmit}
                                        editMode={isPrivateView}
                                        onSubscribeFormSubmit={onSubscribeFormSubmit}
                                        isAboutPage={false}
                                        onAboutPageSelect={() => {}}
                                        smartShareUrl={smartShareUrl}
                                    />
                                )}
                            </>
                        )}
                    </>
                }
                mainContent={
                    <>
                        {isPrivateView && !isBusinessParent && content && "shares" in content && "sum" in content.shares && isArticle && (
                            <V3SharesReportDesktop shares={content.shares} />
                        )}
                        <V3ContentLargeWrapper>
                            {
                                <PublicContentWrapper $wide={isFileContent}>
                                    <VerticalSpacing top={42}>
                                        {content ? (
                                            <>
                                                <ContentTextColumn>
                                                    <V3ContentNavbar
                                                        content={content.article}
                                                        isBusinessParent={isBusinessParent}
                                                        isPrivateView={isPrivateView || false}
                                                        viewOriginal={!content.article.publishedOnAuthory && !!content.article.originalUrl}
                                                        byline={byline}
                                                    />
                                                    {isPrivateView && "collections" in author && "collections" in content.article && (
                                                        <VerticalSpacing bottom={24}>
                                                            <ContentCollectionsPanel
                                                                contentCollections={content.article.collections}
                                                                allCollections={author.collections}
                                                                modifyItemOnCollectionCallback={(add, collectionSlug) => {
                                                                    if (addOrRemoveItemManuallyFromCollection)
                                                                        addOrRemoveItemManuallyFromCollection(
                                                                            add,
                                                                            content.article.canonicalSlug,
                                                                            collectionSlug
                                                                        );
                                                                }}
                                                            />
                                                        </VerticalSpacing>
                                                    )}
                                                    {isPrivateView && !content.article.publishedOnAuthory && (
                                                        <HidePrint>
                                                            <VerticalSpacing bottom={12}>
                                                                <BannerDetails useYellow allowUserThemable>
                                                                    {content.article.effectiveVisibility === Visibility.Preview ? (
                                                                        <>
                                                                            On your public portfolio/collections, this content item links to the original instead
                                                                            of the Authory backup.
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            This content item links to the Authory backup instead of the original
                                                                            piece.
                                                                        </>
                                                                    )}{" "}
                                                                    <InlineLink
                                                                        href="https://authory.com/help/en/articles/9667500-how-does-the-preview-of-content-items-work#h_02b3c1cdb5"
                                                                        target="_blank"
                                                                    >
                                                                        Learn more
                                                                    </InlineLink>
                                                                </BannerDetails>
                                                            </VerticalSpacing>
                                                        </HidePrint>
                                                    )}
                                                    {isPrivateView && hasCustomPreview && (
                                                        <VerticalSpacing bottom={24}>
                                                            <BannerDetails allowUserThemable>
                                                                This content item has a{" "}
                                                                <InlineLink onClick={() => onCustomPreviewOpen && onCustomPreviewOpen()}>
                                                                    {" "}
                                                                    custom preview headline{" "}
                                                                </InlineLink>{" "}
                                                                set.{" "}
                                                                <InlineLink
                                                                    href="https://authory.com/help/en/articles/9667500-how-does-the-preview-of-content-items-work#h_cc824a2476"
                                                                    target="_blank"
                                                                >
                                                                    Learn more
                                                                </InlineLink>
                                                            </BannerDetails>
                                                        </VerticalSpacing>
                                                    )}
                                                    <VerticalSpacing bottom={24}>
                                                        <UserThemableArticleTitle>{content.article.title}</UserThemableArticleTitle>
                                                    </VerticalSpacing>
                                                </ContentTextColumn>
                                                {importInProgress ? (
                                                    <ContentTextColumn>
                                                        <VerticalSpacing top={60}>
                                                            <V3EmptyState
                                                                customPadding="40px 20px"
                                                                title={getImportInProgressTitle(isPrivateView, content.article.contentDetail.type)}
                                                                customDescriptionElem="div"
                                                                description={
                                                                    <VerticalSpacing top={35}>
                                                                        {getImportInProgressDescription(
                                                                            isPrivateView,
                                                                            content.article.contentDetail.type
                                                                        )}
                                                                    </VerticalSpacing>
                                                                }
                                                                svgIcon={
                                                                    <VerticalSpacing bottom={-25}>
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            width="24"
                                                                            height="24"
                                                                            fill="none"
                                                                            stroke="#ABB0BA"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        >
                                                                            <circle cx="12" cy="12" r="10" />
                                                                            <path d="M12 6v6l4 2" />
                                                                        </svg>
                                                                    </VerticalSpacing>
                                                                }
                                                                maxWidth={600}
                                                            >
                                                                <VerticalSpacing top={-14} bottom={30}>
                                                                    <V3BodyGrey48>
                                                                        {isPrivateView ? (
                                                                            "You can leave this page and come back anytime, we’ll finish processing in the background."
                                                                        ) : (
                                                                            <>
                                                                                <strong>Please note:</strong> You'll need to refresh this page to see
                                                                                any updates.
                                                                            </>
                                                                        )}
                                                                    </V3BodyGrey48>
                                                                </VerticalSpacing>
                                                                <ProgressMaterialize>
                                                                    <ProgressInderterminate />
                                                                </ProgressMaterialize>
                                                                {isPrivateView && !isCustomAudioOrCustomVideo && (
                                                                    <VerticalSpacing top={30}>
                                                                        <V3BodyGrey48>
                                                                            <strong>Good to know:</strong> By default, the preview of an article on
                                                                            your portfolio will forward visitors to the original piece. However, in
                                                                            case the original is not available anymore, you can change the settings to
                                                                            forward your visitors to the Authory copy instead.{" "}
                                                                            <ShareSettingsTooltip text="Here is how" />
                                                                        </V3BodyGrey48>
                                                                    </VerticalSpacing>
                                                                )}
                                                            </V3EmptyState>
                                                        </VerticalSpacing>
                                                    </ContentTextColumn>
                                                ) : (
                                                    <VerticalSpacing bottom={120}>
                                                        <V3ContentRenderer content={content.article.contentDetail} />
                                                    </VerticalSpacing>
                                                )}
                                            </>
                                        ) : (
                                            <ContentLoaderWrapper>
                                                <V3Loader />
                                            </ContentLoaderWrapper>
                                        )}
                                    </VerticalSpacing>
                                </PublicContentWrapper>
                            }
                        </V3ContentLargeWrapper>
                    </>
                }
            />
        </>
    );
};
